import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Lock } from "lucide-react";

interface ConfirmarCartelaProps {
  cartela: string;
  laudoId: string;
  onSuccess: () => void;
}

export function ConfirmarCartela({
  cartela,
  laudoId,
  onSuccess,
}: ConfirmarCartelaProps) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { session } = useAuth();

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/laudos/${laudoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ cartelaConfirmada: true }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Erro ao confirmar cartela");
      }

      toast({ title: "Cartela confirmada com sucesso!" });
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro ao confirmar cartela",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="default"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={isLoading}
        className="gap-2 bg-green-600 hover:bg-green-700"
      >
        <Lock className="w-4 h-4" />
        Confirmar Cartela
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Cartela Definitivamente</AlertDialogTitle>
            <AlertDialogDescription>
              Após confirmar, apenas o OWNER poderá alterar a cartela{" "}
              <strong>{cartela}</strong>. Esta ação não pode ser desfeita
              facilmente. Tem certeza?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? "Confirmando..." : "Confirmar Definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
